import React, { useState, useEffect } from 'react';
import { Search, ChevronDown, ChevronLeft, ChevronRight, Download, X, Hash } from 'lucide-react';
import * as XLSX from 'xlsx';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api/v1';

// Función para formatear fecha como YYYY-MM-DD local (evita desfases de zona horaria)
const formatDate = (date) => {
    if (!date) return '';
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

export default function DocenteAsistenciaPreadmision() {
    const [filters, setFilters] = useState({
        tipo_preadmision: 'curso',
        id_asignatura: '',
        id_paralelo: ''
    });

    const [asignaturas, setAsignaturas] = useState([]);
    const [paralelos, setParalelos] = useState([]);
    const [estudiantes, setEstudiantes] = useState([]);
    const [asistencias, setAsistencias] = useState({});
    const [diasConAsistencia, setDiasConAsistencia] = useState([]);

    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDay, setSelectedDay] = useState(new Date());
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showCalendar, setShowCalendar] = useState(true);

    // Totalizador
    const [totalizerStudent, setTotalizerStudent] = useState('');
    const [monthlyAttendances, setMonthlyAttendances] = useState([]);

    // Modal de descarga
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [downloadDates, setDownloadDates] = useState({
        fecha_desde: '',
        fecha_hasta: ''
    });

    // Cargar asignaturas del docente (preadmisión)
    useEffect(() => {
        loadAsignaturas();
    }, [filters.tipo_preadmision]);

    // Cargar paralelos cuando cambia asignatura
    useEffect(() => {
        if (filters.id_asignatura) {
            loadParalelos(filters.id_asignatura);
        } else {
            setParalelos([]);
        }
        // Limpiar el totalizador cuando cambia la asignatura
        setTotalizerStudent('');
    }, [filters.id_asignatura]);

    // Cargar estudiantes y días con asistencia cuando cambia filtros o mes
    useEffect(() => {
        if (filters.id_asignatura) {
            loadEstudiantes();
            loadDiasConAsistencia();
        }
    }, [filters.id_asignatura, filters.id_paralelo, currentDate]);

    // Recargar asistencias cuando cambia el día seleccionado
    useEffect(() => {
        if (filters.id_asignatura) {
            loadAsistenciasDelDia();
            loadMonthlyAttendances();
        }
    }, [selectedDay, filters.id_asignatura, filters.id_paralelo]);

    const loadAsignaturas = async () => {
        try {
            const token = localStorage.getItem('token');
            const tipoAPI = filters.tipo_preadmision === 'prueba' ? 'admision' : 'preadmision';
            const response = await fetch(`${API_URL}/docente/asignaturas?tipo=${tipoAPI}&modalidad=todas`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setAsignaturas(data.asignaturas || []);
        } catch (error) {
            console.error('Error loading asignaturas:', error);
            // Fallback: cargar todas las asignaturas
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_URL}/docente/asignaturas`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });
                const data = await response.json();
                setAsignaturas(data.asignaturas || []);
            } catch (e) {
                console.error('Error loading fallback asignaturas:', e);
            }
        }
    };

    const loadParalelos = async (id_asignatura) => {
        try {
            const token = localStorage.getItem('token');
            const tipoAPI = filters.tipo_preadmision === 'prueba' ? 'admision' : 'preadmision';
            const response = await fetch(`${API_URL}/docente/paralelos/${id_asignatura}?tipo=${tipoAPI}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });
            const data = await response.json();
            setParalelos(data.paralelos || []);
        } catch (error) {
            console.error('Error loading paralelos:', error);
        }
    };

    const loadEstudiantes = async () => {
        try {
            setLoading(true);
            const tipoAPI = filters.tipo_preadmision === 'prueba' ? 'admision' : 'preadmision';
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                tipo: tipoAPI,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo })
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/estudiantes?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setEstudiantes(data.estudiantes || []);

            // Cargar asistencias del día seleccionado
            await loadAsistenciasDelDia();
        } catch (error) {
            console.error('Error loading estudiantes:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadDiasConAsistencia = async () => {
        try {
            const year = currentDate.getFullYear();
            const month = currentDate.getMonth();
            const primerDia = formatDate(new Date(year, month, 1));
            const ultimoDia = formatDate(new Date(year, month + 1, 0));
            const tipoAPI = filters.tipo_preadmision === 'prueba' ? 'admision' : 'preadmision';

            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo }),
                fecha_desde: primerDia,
                fecha_hasta: ultimoDia,
                tipo: tipoAPI,
                gestion: year
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/asistencias/dias?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setDiasConAsistencia(data.dias || []);
        } catch (error) {
            console.error('Error loading dias con asistencia:', error);
            setDiasConAsistencia([]);
        }
    };

    const loadAsistenciasDelDia = async () => {
        try {
            const fecha = formatDate(selectedDay);
            const tipoAPI = filters.tipo_preadmision === 'prueba' ? 'admision' : 'preadmision';
            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo }),
                fecha_desde: fecha,
                fecha_hasta: fecha,
                tipo: tipoAPI
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/asistencias?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            // Convertir array a objeto {id_estudiante: { tipo_asistencia, es_licencia_automatica }}
            const asistenciasMap = {};
            (data.asistencias || []).forEach(a => {
                asistenciasMap[String(a.id_estudiante)] = {
                    tipo: a.tipo_asistencia,
                    isAuto: a.es_licencia_automatica
                };
            });
            setAsistencias(asistenciasMap);
        } catch (error) {
            console.error('Error loading asistencias:', error);
        }
    };

    const loadMonthlyAttendances = async () => {
        if (!filters.id_asignatura) return;
        try {
            const year = selectedDay.getFullYear();
            const month = selectedDay.getMonth();
            const firstDay = formatDate(new Date(year, month, 1));
            const lastDay = formatDate(new Date(year, month + 1, 0)); // Último día del mes
            const tipoAPI = filters.tipo_preadmision === 'prueba' ? 'admision' : 'preadmision';

            const query = new URLSearchParams({
                id_asignatura: filters.id_asignatura,
                ...(filters.id_paralelo && { id_paralelo: filters.id_paralelo }),
                fecha_desde: firstDay,
                fecha_hasta: lastDay,
                tipo: tipoAPI,
                gestion: year
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/asistencias?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            setMonthlyAttendances(data.asistencias || []);
        } catch (error) {
            console.error('Error loading monthly attendances:', error);
        }
    };

    const handleAsistenciaChange = (id_estudiante, valor) => {
        setAsistencias(prev => ({
            ...prev,
            [String(id_estudiante)]: {
                ...(prev[String(id_estudiante)] || {}),
                tipo: valor
            }
        }));
    };

    const handleGuardar = async () => {
        try {
            setSaving(true);
            const fecha = formatDate(selectedDay);
            const gestion = selectedDay.getFullYear();
            const tipoAPI = filters.tipo_preadmision === 'prueba' ? 'admision' : 'preadmision';

            // Guardar cada asistencia
            const results = await Promise.all(estudiantes.map(async (est) => {
                const asisData = asistencias[String(est.id_estudiante)] || {};
                const tipo_asistencia = asisData.tipo || 'N';
                try {
                    const res = await fetch(`${API_URL}/docente/asistencias`, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        body: JSON.stringify({
                            id_estudiante: est.id_estudiante,
                            id_asignatura: filters.id_asignatura,
                            id_paralelo: filters.id_paralelo || null,
                            fecha,
                            tipo_asistencia,
                            gestion,
                            tipo: tipoAPI
                        })
                    });
                    return res.ok;
                } catch (error) {
                    console.error('Error in batch save:', error);
                    return false;
                }
            }));

            if (results.every(r => r)) {
                alert('Asistencias guardadas exitosamente');
                await loadAsistenciasDelDia();
                await loadDiasConAsistencia();
                await loadMonthlyAttendances();
            } else {
                alert('Hubo problemas al guardar algunas asistencias. Por favor, verifique su conexión e intente de nuevo.');
            }
        } catch (error) {
            console.error('Error saving asistencias:', error);
            alert('Error al procesar el guardado de asistencias');
        } finally {
            setSaving(false);
        }
    };

    const handleDownload = async () => {
        if (!downloadDates.fecha_desde || !downloadDates.fecha_hasta) {
            alert('Debe seleccionar un rango de fechas');
            return;
        }

        try {
            const tipoAPI = filters.tipo_preadmision === 'prueba' ? 'admision' : 'preadmision';
            const query = new URLSearchParams({
                fecha_desde: downloadDates.fecha_desde,
                fecha_hasta: downloadDates.fecha_hasta,
                tipo: tipoAPI
            });

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/docente/asistencias/export?${query}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();

            if (data.asistencias && data.asistencias.length > 0) {
                const ws = XLSX.utils.json_to_sheet(data.asistencias);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Asistencias');
                XLSX.writeFile(wb, `asistencias_preadmision_${downloadDates.fecha_desde}_${downloadDates.fecha_hasta}.xlsx`);
                setShowDownloadModal(false);
            } else {
                alert('No hay asistencias en el rango seleccionado');
            }
        } catch (error) {
            console.error('Error downloading:', error);
            alert('Error al descargar asistencias');
        }
    };

    // Generar calendario
    const generateCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();
        const firstDay = new Date(year, month, 1).getDay();
        const daysInMonth = new Date(year, month + 1, 0).getDate();

        const weeks = [];
        let week = new Array(7).fill(null);
        let dayCounter = 1;

        for (let i = firstDay; i < 7 && dayCounter <= daysInMonth; i++) {
            week[i] = dayCounter++;
        }
        weeks.push([...week]);

        while (dayCounter <= daysInMonth) {
            week = new Array(7).fill(null);
            for (let i = 0; i < 7 && dayCounter <= daysInMonth; i++) {
                week[i] = dayCounter++;
            }
            weeks.push([...week]);
        }

        return weeks;
    };

    const monthName = currentDate.toLocaleString('es', { month: 'long', year: 'numeric' }).toUpperCase();
    const weeks = generateCalendar();
    const dayNames = ['D', 'L', 'M', 'M', 'J', 'V', 'S'];

    const changeMonth = (direction) => {
        const newDate = new Date(currentDate);
        newDate.setMonth(currentDate.getMonth() + direction);
        setCurrentDate(newDate);
    };

    const handleDaySelect = (day) => {
        if (!day) return;
        const newSelectedDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
        setSelectedDay(newSelectedDay);
    };

    const isDayWithAttendance = (day) => {
        if (!day) return false;
        const fecha = formatDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
        return diasConAsistencia.includes(fecha);
    };

    const isSelectedDay = (day) => {
        return day === selectedDay.getDate() &&
            currentDate.getMonth() === selectedDay.getMonth() &&
            currentDate.getFullYear() === selectedDay.getFullYear();
    };

    // Filtrar estudiantes por búsqueda
    const filteredEstudiantes = estudiantes.filter(est => {
        const searchLower = searchTerm.toLowerCase();
        return (
            (est.codigo || '').toLowerCase().includes(searchLower) ||
            (est.nombres || '').toLowerCase().includes(searchLower) ||
            (est.apellidos || est.apellido_paterno || '').toLowerCase().includes(searchLower)
        );
    });

    return (
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-xl font-bold text-gray-800">Docente &gt; Asistencia (Preadmisión)</h1>
            </div>

            {/* Filtros */}
            <div className="flex flex-wrap gap-3 mb-6">
                {/* Tipo Preadmisión */}
                <div className="relative">
                    <select
                        value={filters.tipo_preadmision}
                        onChange={(e) => setFilters({ ...filters, tipo_preadmision: e.target.value, id_asignatura: '', id_paralelo: '' })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[200px]"
                    >
                        <option value="curso">Curso de Pre Admisión</option>
                        <option value="prueba">Prueba de Admisión</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                {/* Asignatura */}
                <div className="relative">
                    <select
                        value={filters.id_asignatura}
                        onChange={(e) => setFilters({ ...filters, id_asignatura: e.target.value, id_paralelo: '' })}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[200px] max-w-md"
                    >
                        <option value="">Asignatura</option>
                        {asignaturas.map(a => (
                            <option key={a.id_asignatura} value={a.id_asignatura}>
                                {a.nombre} {a.modalidad ? `(${a.modalidad})` : ''} - {a.codigo}
                            </option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                {/* Paralelo */}
                <div className="relative">
                    <select
                        value={filters.id_paralelo}
                        onChange={(e) => setFilters({ ...filters, id_paralelo: e.target.value })}
                        disabled={!filters.id_asignatura}
                        className="appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-full bg-white text-sm min-w-[120px] disabled:bg-gray-100"
                    >
                        <option value="">Paralelo</option>
                        {paralelos.map(p => (
                            <option key={p.id_paralelo || p.paralelo} value={p.id_paralelo || p.paralelo}>{p.paralelo || p.nombre}</option>
                        ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={18} />
                </div>

                {/* Buscador */}
                <div className="relative flex-1 max-w-xs">
                    <input
                        type="text"
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-4 pr-10 py-2 border border-gray-300 rounded-full text-sm"
                    />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                </div>

                {/* Botón Descarga */}
                <button
                    onClick={() => setShowDownloadModal(true)}
                    className="p-2 bg-blue-900 text-white rounded-full hover:bg-blue-800 transition-colors"
                    title="Descargar asistencias"
                >
                    <Download size={20} />
                </button>

                {/* Botón Ocultar Calendario */}
                <button
                    onClick={() => setShowCalendar(!showCalendar)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${showCalendar
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}
                >
                    {showCalendar ? 'Ocultar Calendario' : 'Mostrar Calendario'}
                </button>
            </div>

            {/* Contenido principal */}
            <div className="flex flex-wrap gap-6">
                {/* Calendario */}
                {showCalendar && (
                    <div className="bg-white rounded-lg shadow p-4 w-full md:w-auto">
                        <div className="flex items-center justify-between mb-4">
                            <button onClick={() => changeMonth(-1)} className="p-1 hover:bg-gray-100 rounded">
                                <ChevronLeft size={20} />
                            </button>
                            <span className="font-semibold text-gray-800">{monthName}</span>
                            <button onClick={() => changeMonth(1)} className="p-1 hover:bg-gray-100 rounded">
                                <ChevronRight size={20} />
                            </button>
                        </div>

                        <table className="w-full text-center text-sm">
                            <thead>
                                <tr>
                                    {dayNames.map((d, i) => (
                                        <th key={i} className="p-2 text-gray-500 font-normal">{d}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {weeks.map((week, wi) => (
                                    <tr key={wi}>
                                        {week.map((day, di) => (
                                            <td key={di} className="p-1">
                                                {day && (
                                                    <button
                                                        onClick={() => handleDaySelect(day)}
                                                        className={`w-8 h-8 rounded-full text-sm transition-colors
                            ${isSelectedDay(day) ? 'bg-blue-600 text-white' :
                                                                isDayWithAttendance(day) ? 'bg-blue-100 text-blue-600 font-medium' :
                                                                    'hover:bg-gray-100 text-gray-700'}`}
                                                    >
                                                        {day}
                                                    </button>
                                                )}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>

                        <p className="mt-4 text-xs text-gray-500">N=No marcado - P=Presente - F=Falta - L=Licencia</p>
                    </div>
                )}

                {/* Tabla de asistencia */}
                <div className="flex-1 bg-white rounded-lg shadow overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-gray-50 border-b">
                                <tr>
                                    <th className="px-3 py-3 border text-center font-semibold">No</th>
                                    <th className="px-3 py-3 border text-center font-semibold">Codigo</th>
                                    <th className="px-3 py-3 border text-center font-semibold">Nombres</th>
                                    <th className="px-3 py-3 border text-center font-semibold">Apellidos</th>
                                    <th className="px-3 py-3 border text-center font-semibold">Asistencia</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y">
                                {loading ? (
                                    <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">Cargando...</td></tr>
                                ) : filteredEstudiantes.length === 0 ? (
                                    <tr><td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                                        {filters.id_asignatura ? 'No hay estudiantes para mostrar' : 'Seleccione una asignatura'}
                                    </td></tr>
                                ) : (
                                    filteredEstudiantes.map((est, index) => (
                                        <tr key={est.id_estudiante} className="hover:bg-gray-50">
                                            <td className="px-3 py-3 border text-center">{index + 1}</td>
                                            <td className="px-3 py-3 border text-center">{est.codigo || est.codigo_estudiante || '-'}</td>
                                            <td className="px-3 py-3 border text-center">{est.nombres || '-'}</td>
                                            <td className="px-3 py-3 border text-center">{est.apellidos || `${est.apellido_paterno || ''} ${est.apellido_materno || ''}`.trim() || '-'}</td>
                                            <td className="px-3 py-3 border text-center">
                                                <div className="flex flex-col items-center gap-1">
                                                    <div className="relative inline-block">
                                                        <select
                                                            value={asistencias[String(est.id_estudiante)]?.tipo || 'N'}
                                                            onChange={(e) => handleAsistenciaChange(est.id_estudiante, e.target.value)}
                                                            disabled={asistencias[String(est.id_estudiante)]?.isAuto}
                                                            className={`appearance-none pl-3 pr-8 py-1 border border-gray-300 rounded bg-white text-sm ${asistencias[String(est.id_estudiante)]?.isAuto ? 'bg-gray-100 text-blue-600 font-bold border-blue-200' : ''}`}
                                                        >
                                                            <option value="N">N</option>
                                                            <option value="P">P</option>
                                                            <option value="F">F</option>
                                                        </select>
                                                        {!asistencias[String(est.id_estudiante)]?.isAuto && (
                                                            <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={14} />
                                                        )}
                                                    </div>
                                                    {asistencias[String(est.id_estudiante)]?.isAuto && (
                                                        <span className="text-[10px] font-bold text-blue-600 uppercase tracking-tighter bg-blue-50 px-1 rounded border border-blue-100">
                                                            Licencia Aprobada
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Botón Guardar */}
                    {filteredEstudiantes.length > 0 && (
                        <div className="p-4 border-t flex justify-end">
                            <button
                                onClick={handleGuardar}
                                disabled={saving}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800 disabled:opacity-50"
                            >
                                {saving ? 'Guardando...' : 'Guardar Asistencia'}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Totalizador de Asistencia */}
            {filters.id_asignatura && estudiantes.length > 0 && (
                <div className="mt-8 bg-white rounded-lg shadow p-6 border-t-4 border-blue-600">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                        <div>
                            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                                Totalizador Mensual
                                <span className="text-sm font-normal text-gray-500">
                                    (del mes completo de {selectedDay.toLocaleString('es', { month: 'long' })})
                                </span>
                            </h2>
                        </div>

                        <div className="relative min-w-[300px]">
                            <label className="block text-xs font-semibold text-gray-500 uppercase mb-1 ml-1">Filtrar por Estudiante</label>
                            <select
                                value={totalizerStudent}
                                onChange={(e) => setTotalizerStudent(e.target.value)}
                                className="w-full appearance-none pl-4 pr-10 py-2 border border-gray-300 rounded-lg bg-gray-50 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
                            >
                                <option value="">-- Todos los Estudiantes --</option>
                                {estudiantes.map(est => (
                                    <option key={est.id_estudiante} value={est.id_estudiante}>
                                        {est.apellidos || `${est.apellido_paterno || ''} ${est.apellido_materno || ''}`.trim() || ''} {est.nombres || ''} ({est.codigo || est.codigo_estudiante || 'S/C'})
                                    </option>
                                ))}
                            </select>
                            <ChevronDown className="absolute right-3 bottom-2.5 text-gray-400 pointer-events-none" size={18} />
                        </div>
                    </div>

                    {(() => {
                        const filtered = totalizerStudent
                            ? monthlyAttendances.filter(a => String(a.id_estudiante) === String(totalizerStudent))
                            : monthlyAttendances;

                        const p = filtered.filter(a => String(a.tipo_asistencia).toUpperCase() === 'P').length;
                        const f = filtered.filter(a => String(a.tipo_asistencia).toUpperCase() === 'F').length;
                        const l = filtered.filter(a => String(a.tipo_asistencia).toUpperCase() === 'L' || String(a.tipo_asistencia).toUpperCase() === 'LP').length;
                        const n = filtered.filter(a => String(a.tipo_asistencia).toUpperCase() === 'N').length;
                        const total = p + f + l + n;
                        const calcPct = (val) => total > 0 ? ((val / total) * 100).toFixed(1) : '0';

                        return (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                                {/* Presentes */}
                                <div className="bg-green-50 rounded-xl p-4 border border-green-100 flex items-center justify-between shadow-sm">
                                    <div>
                                        <p className="text-green-600 text-[10px] font-bold uppercase tracking-wider mb-0.5">Presentes (P)</p>
                                        <h3 className="text-2xl font-black text-green-700 leading-tight">{p}</h3>
                                        <p className="text-[10px] font-bold text-green-600/70 mt-0.5">{calcPct(p)}% del total</p>
                                    </div>
                                    <div className="bg-green-100 p-2.5 rounded-full text-green-600">
                                        <Search size={20} />
                                    </div>
                                </div>

                                {/* Faltas */}
                                <div className="bg-red-50 rounded-xl p-4 border border-red-100 flex items-center justify-between shadow-sm">
                                    <div>
                                        <p className="text-red-600 text-[10px] font-bold uppercase tracking-wider mb-0.5">Faltas (F)</p>
                                        <h3 className="text-2xl font-black text-red-700 leading-tight">{f}</h3>
                                        <p className="text-[10px] font-bold text-red-600/70 mt-0.5">{calcPct(f)}% del total</p>
                                    </div>
                                    <div className="bg-red-100 p-2.5 rounded-full text-red-600">
                                        <X size={20} />
                                    </div>
                                </div>

                                {/* Licencias */}
                                <div className="bg-blue-50 rounded-xl p-4 border border-blue-100 flex items-center justify-between shadow-sm">
                                    <div>
                                        <p className="text-blue-600 text-[10px] font-bold uppercase tracking-wider mb-0.5">Licencias (L)</p>
                                        <h3 className="text-2xl font-black text-blue-700 leading-tight">{l}</h3>
                                        <p className="text-[10px] font-bold text-blue-600/70 mt-0.5">{calcPct(l)}% del total</p>
                                    </div>
                                    <div className="bg-blue-100 p-2.5 rounded-full text-blue-600">
                                        <ChevronRight size={20} />
                                    </div>
                                </div>

                                {/* Total */}
                                <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 flex items-center justify-between shadow-sm">
                                    <div>
                                        <p className="text-gray-600 text-[10px] font-bold uppercase tracking-wider mb-0.5">Total Registros</p>
                                        <h3 className="text-2xl font-black text-gray-800 leading-tight">{total}</h3>
                                        <p className="text-[10px] font-bold text-gray-400 mt-0.5">100% procesado</p>
                                    </div>
                                    <div className="bg-gray-200 p-2.5 rounded-full text-gray-600">
                                        <Hash size={20} />
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    <div className="mt-4 text-xs text-gray-400 italic">
                        * Los totales mostrados corresponden a la sumatoria de asistencias registradas para todo el mes actual seleccionado.
                    </div>
                </div>
            )}

            {/* Modal de Descarga */}
            {showDownloadModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                        <div className="flex justify-between items-center px-6 py-4 border-b">
                            <h2 className="text-xl font-bold text-blue-900">Descargar Asistencias</h2>
                            <button onClick={() => setShowDownloadModal(false)} className="text-gray-400 hover:text-gray-600">
                                <X size={24} />
                            </button>
                        </div>
                        <div className="p-6">
                            <div className="mb-4">
                                <label className="block text-sm text-gray-700 mb-2">Fecha desde</label>
                                <input
                                    type="date"
                                    value={downloadDates.fecha_desde}
                                    onChange={(e) => setDownloadDates({ ...downloadDates, fecha_desde: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div className="mb-4">
                                <label className="block text-sm text-gray-700 mb-2">Fecha hasta</label>
                                <input
                                    type="date"
                                    value={downloadDates.fecha_hasta}
                                    onChange={(e) => setDownloadDates({ ...downloadDates, fecha_hasta: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <p className="text-sm text-gray-500 mb-4">
                                Se descargarán las asistencias de todas las asignaturas y paralelos asignados al docente.
                            </p>
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t gap-3">
                            <button
                                onClick={() => setShowDownloadModal(false)}
                                className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={handleDownload}
                                className="px-6 py-2 bg-blue-900 text-white rounded-md hover:bg-blue-800"
                            >
                                Descargar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
